/**
 * SMTPInfoModal Component - Modal with SMTP configuration tips
 * Uses createPortal to render at document.body level, ensuring it
 * always appears in front regardless of parent z-index/overflow.
 */
import React from 'react';
import { createPortal } from 'react-dom';
import { Info, X } from 'lucide-react';

interface SMTPInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SMTPInfoModal: React.FC<SMTPInfoModalProps> = ({
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Info className="text-white" size={24} />
            <h3 className="text-lg font-semibold text-white">Dicas de Configuração SMTP</h3>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-1 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Informações Gerais */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border-2 border-blue-200 dark:border-blue-700">
            <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">📧 Configuração Geral</h4>
            <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
              <li><strong>Email SMTP:</strong> O endereço de email que será usado para enviar as notificações do sistema.</li>
              <li><strong>Senha:</strong> A senha do email ou "App Password" (senha de aplicativo) fornecida pelo seu provedor.</li>
              <li><strong>Servidor SMTP:</strong> O endereço do servidor de email do seu provedor.</li>
              <li><strong>Porta:</strong> A porta de conexão (geralmente 587 para TLS ou 465 para SSL).</li>
              <li><strong>TLS:</strong> Conexão segura - recomendado manter ativado.</li>
            </ul>
          </div>

          {/* Provedores Comuns */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">🌐 Configurações por Provedor</h4>
            <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
              <div className="p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                <strong>Gmail:</strong> smtp.gmail.com | Porta: 587 | TLS: Sim
                <p className="text-xs text-gray-500 mt-1">Requer "Senha de App" (16 caracteres) - Acesse: Conta Google → Segurança → Senhas de app</p>
              </div>
              <div className="p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                <strong>Outlook/Hotmail:</strong> smtp.office365.com | Porta: 587 | TLS: Sim
              </div>
              <div className="p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                <strong>Yahoo:</strong> smtp.mail.yahoo.com | Porta: 587 | TLS: Sim
              </div>
              <div className="p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                <strong>Servidor próprio:</strong> Consulte seu administrador de TI para as configurações corretas.
              </div>
            </div>
          </div>

          {/* Dica de Segurança */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-700">
            <h4 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-2">⚠️ Dica de Segurança</h4>
            <p className="text-sm text-yellow-700 dark:text-yellow-400">
              Para maior segurança, recomendamos usar uma "Senha de Aplicativo" ao invés da senha principal do email.
              A maioria dos provedores oferece essa opção nas configurações de segurança da conta.
            </p>
          </div>

          {/* Footer */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg transition"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SMTPInfoModal;
